/**
 * Employee Other/Miscellaneous Tab Component
 *
 * Contains: Emergency Contact, Notes & Miscellaneous Documents (Sonstige Dokumente).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { FileText, Plus, Trash2, ExternalLink, Loader2, Upload } from 'lucide-react';
import { formatDateDE } from './helpers';

const OtherTab = ({
  formData,
  handleChange,
  handleEmergencyContactChange,
  newDocument,
  setNewDocument,
  handleDocumentUpload,
  addDocument,
  removeDocument,
  uploadingDocument,
}) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  return (
    <div className="space-y-6">
      {/* Emergency Contact & Notes */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="font-heading">Sonstiges</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Emergency Contact */}
          <div className="space-y-4">
            <Label className="text-base font-medium">Notfallkontakt</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergency_name">Name</Label>
                <Input
                  id="emergency_name"
                  value={formData.emergency_contact?.name || ''}
                  onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                  data-testid="emergency-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_relationship">Beziehung</Label>
                <Input
                  id="emergency_relationship"
                  value={formData.emergency_contact?.relationship || ''}
                  onChange={(e) => handleEmergencyContactChange('relationship', e.target.value)}
                  placeholder="z.B. Ehepartner"
                  data-testid="emergency-relationship"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_phone">Telefon</Label>
                <Input
                  id="emergency_phone"
                  value={formData.emergency_contact?.phone || ''}
                  onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
                  data-testid="emergency-phone"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Interne Notizen zum Mitarbeiter..."
              rows={4}
              data-testid="employee-notes"
            />
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card className="border-border/60" data-testid="documents-card">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Sonstige Dokumente
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Existing Documents */}
          {formData.documents?.length > 0 ? (
            <div className="space-y-3">
              <Label>Hochgeladene Dokumente</Label>
              {formData.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-start justify-between p-4 rounded-lg bg-secondary/30"
                  data-testid={`document-item-${doc.id}`}
                >
                  <div className="space-y-1 min-w-0">
                    {doc.title && (
                      <p className="font-medium truncate">{doc.title}</p>
                    )}
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}?token=${token}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline break-all"
                    >
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{doc.filename}</span>
                    </a>
                    {doc.uploaded_at && (
                      <p className="text-xs text-muted-foreground">
                        Hochgeladen: {formatDateDE(doc.uploaded_at)}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeDocument(doc.id)}
                    className="text-destructive hover:text-destructive flex-shrink-0"
                    data-testid={`remove-document-${doc.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Noch keine Dokumente hochgeladen.
            </p>
          )}

          {/* Add New Document */}
          <div className="space-y-4 p-4 rounded-lg border border-dashed border-border">
            <Label className="text-base font-medium">Neues Dokument hinzufügen</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="document_title">Titel (optional)</Label>
                <Input
                  id="document_title"
                  value={newDocument?.title || ''}
                  onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                  placeholder="z.B. Personalbogen, Elternzeitbescheinigung"
                  data-testid="document-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Datei hochladen (PDF, Bild, Word, Excel)</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={handleDocumentUpload}
                    disabled={uploadingDocument}
                    className="flex-1"
                    data-testid="document-file-input"
                  />
                  {uploadingDocument && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
                {newDocument?.filename && (
                  <p className="text-sm text-green-600 truncate">
                    ✓ {newDocument.filename}
                  </p>
                )}
              </div>
            </div>
            <Button
              type="button"
              onClick={addDocument}
              variant="outline"
              className="w-full"
              disabled={!newDocument?.url || uploadingDocument}
              data-testid="add-document-btn"
            >
              <Plus className="mr-2 h-4 w-4" />
              Dokument hinzufügen
            </Button>
            <p className="text-xs text-muted-foreground flex items-start gap-1">
              <Upload className="h-3 w-3 mt-0.5 flex-shrink-0" />
              Max. 10 MB pro Datei. Erlaubt: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OtherTab;
